"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const d3_scale_1 = require("d3-scale");
const d3_shape_1 = require("d3-shape");
const withParentDimensions_1 = require("../generic/withParentDimensions");
const useDeepMemo_1 = require("../../hooks/useDeepMemo");
const lodash_1 = require("lodash");
const common_styled_components_1 = require("../common-styled-components");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../theme");
const amountUtils_1 = require("../../util/amountUtils");
const statisticsUtils_1 = require("../../util/statisticsUtils");
const react_1 = require("react");
const getSVGPoint_1 = require("../generic/getSVGPoint");
const Tooltip_1 = require("../generic/Tooltip");
const timeUtils_1 = require("../../util/timeUtils");
const LimitLine = styled_components_1.default.line `
  stroke-dasharray: 5 2;
  stroke-width: 1px;
  fill: none;
`;
exports.default = (0, withParentDimensions_1.withParentDimensions)(({ data, limits, title, unit, poiData, poiDataSetName, parentDimensions: { width }, }) => {
    const HEIGHT = 200;
    const MARGINS = {
        top: 18,
        bottom: 20,
        left: 80,
        right: 25,
    };
    const CONTENT_BOTTOM = HEIGHT - MARGINS.bottom;
    const TICK_FONT_SIZE = 12;
    const svgRef = (0, react_1.useRef)(null);
    const [tooltipData, setTooltipData] = (0, react_1.useState)(undefined);
    const [scaleX, scaleY, lineGenerator, divider, sortedData, poisWithValues] = (0, useDeepMemo_1.useDeepMemo)(() => {
        const allDateValues = (0, lodash_1.flatMap)(data, (d) => d.data).concat(data.map((d) => d.prediction).filter((d) => d));
        const timeValues = allDateValues.map((d) => d.date.getTime());
        const minTime = Math.min(...timeValues);
        const maxTime = Math.max(...timeValues);
        const scaleX = (0, d3_scale_1.scaleTime)()
            .range([MARGINS.left, width - MARGINS.right])
            .domain([new Date(minTime), new Date(maxTime)]);
        const values = allDateValues
            .map((d) => d.value)
            .filter((d) => d !== undefined && !isNaN(d));
        const limitValues = limits?.map((l) => l.value) ?? [];
        const allValues = values.concat(limitValues);
        const minY = Math.min(...allValues);
        const maxY = Math.max(...allValues);
        const divider = (0, amountUtils_1.getDivider)(maxY) ?? 1;
        const scaleY = (0, d3_scale_1.scaleLinear)()
            .range([CONTENT_BOTTOM, MARGINS.top])
            .domain([minY, maxY])
            .nice();
        const lineGenerator = (0, d3_shape_1.line)()
            .x((d) => scaleX(d.date))
            .defined((d) => d !== undefined && !isNaN(d.value))
            .y((d) => (d.value !== undefined ? scaleY(d.value) : 0))
            .curve(d3_shape_1.curveLinear);
        const sortedData = data.map((series) => ({
            ...series,
            data: (0, lodash_1.sortBy)(series.data, (d) => new Date(d.date)),
        }));
        const poiDataSet = data.find((d) => d.name === poiDataSetName)?.data ??
            sortedData[0].data;
        const poisAndValues = poiData?.map((poi) => ({
            ...poi,
            value: poi.value ?? (0, statisticsUtils_1.getClosestDateValue)(poiDataSet, poi.date)?.value,
        }));
        return [
            scaleX,
            scaleY,
            lineGenerator,
            divider,
            sortedData,
            poisAndValues,
        ];
    }, [data, poiData, limits, width]);
    const zeroLine = scaleY(0);
    const zeroLineInBounds = zeroLine >= MARGINS.top && zeroLine <= HEIGHT - MARGINS.bottom;
    const mouseMoved = (e) => {
        e.preventDefault();
        const svgPoint = (0, getSVGPoint_1.getSVGPoint)(e.clientX, e.clientY, svgRef);
        if (svgPoint) {
            const date = scaleX.invert(svgPoint.x);
            const latestData = sortedData.map((series) => {
                return {
                    series: series.name,
                    color: series.color,
                    dateValue: (0, statisticsUtils_1.getLatestDateValue)(series.data, date),
                };
            });
            const latestDate = (0, lodash_1.maxBy)(latestData, (d) => d.dateValue?.date.getTime())
                ?.dateValue?.date;
            if (!latestDate) {
                return;
            }
            const predictionDate = (0, lodash_1.chain)(sortedData)
                .map((s) => s.prediction?.date)
                .filter((d) => d !== undefined)
                .uniqBy((d) => d.getTime())
                .valueOf();
            const tooltipData = {
                point: svgPoint,
                containerSize: {
                    width,
                    height: HEIGHT,
                },
                data: [],
            };
            if (predictionDate?.length &&
                Math.abs(predictionDate[0].getTime() - date.getTime()) <
                    Math.abs(latestDate.getTime() - date.getTime())) {
                tooltipData.data = sortedData.map((series) => ({
                    color: series.color,
                    series: series.name,
                    dateValue: series.prediction,
                }));
                tooltipData.isPrediction = true;
            }
            else {
                tooltipData.data = latestData;
            }
            setTooltipData(tooltipData);
        }
    };
    const mouseOut = (e) => {
        e.preventDefault();
        setTooltipData(undefined);
    };
    return ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.GraphTitle, { children: title }), (0, jsx_runtime_1.jsxs)("div", { style: { position: "relative" }, children: [(0, jsx_runtime_1.jsxs)("svg", { width: width, height: HEIGHT, onMouseMove: mouseMoved, onMouseOut: mouseOut, ref: svgRef, children: [scaleX.ticks(Math.round(width / 120)).map((t) => {
                                const tickX = scaleX(t);
                                const tickLength = 6;
                                return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: tickX, x2: tickX, y1: CONTENT_BOTTOM, y2: CONTENT_BOTTOM + tickLength, strokeWidth: 1, stroke: "#aaa" }), (0, jsx_runtime_1.jsx)("text", { x: tickX, textAnchor: "middle", y: CONTENT_BOTTOM + tickLength + TICK_FONT_SIZE, fontSize: TICK_FONT_SIZE, children: (0, timeUtils_1.formatDate)(t) })] }, t.toString()));
                            }), scaleY.ticks(4).map((t) => {
                                const tickY = scaleY(t);
                                const tickLength = 6;
                                return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: width - MARGINS.right, x2: MARGINS.left - tickLength, y1: tickY, y2: tickY, strokeWidth: 1, stroke: theme_1.theme.colors.grey(4), className: "stroke-gray-200" }), (0, jsx_runtime_1.jsxs)("text", { x: MARGINS.left - tickLength, textAnchor: "end", y: tickY + TICK_FONT_SIZE / 2 - 2, fontSize: TICK_FONT_SIZE, children: [(0, amountUtils_1.formatNumber)(t / divider), (0, amountUtils_1.getDividerLabel)(divider)] })] }, t.toString()));
                            }), zeroLineInBounds && ((0, jsx_runtime_1.jsx)("line", { strokeWidth: 1, stroke: "#aaa", x1: MARGINS.left, x2: width - MARGINS.right, y1: zeroLine, y2: zeroLine })), (0, jsx_runtime_1.jsx)("line", { strokeWidth: 1, stroke: "#aaa", x1: MARGINS.left, x2: MARGINS.left, y1: CONTENT_BOTTOM, y2: 0 }), (0, jsx_runtime_1.jsx)("text", { x: MARGINS.left + 4, y: MARGINS.top - 3, fontSize: TICK_FONT_SIZE, children: unit }), limits?.map((limit) => ((0, jsx_runtime_1.jsx)(LimitLine, { x1: MARGINS.left, x2: width - MARGINS.right, y1: scaleY(limit.value), y2: scaleY(limit.value), stroke: limit.color }, limit.label))), sortedData.map((series) => {
                                const lastInSeries = series.data[series.data.length - 1];
                                return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("path", { d: lineGenerator(series.data) ?? "", fill: "none", stroke: series.color, strokeWidth: 1.5, strokeDasharray: series.dashed ? "3 2" : undefined, strokeLinejoin: "round", strokeLinecap: "round" }), series.prediction &&
                                            series.data.length > 0 &&
                                            lastInSeries.value !== undefined && ((0, jsx_runtime_1.jsx)("line", { fill: "none", stroke: series.color, strokeDasharray: "2 2", strokeWidth: 1.5, opacity: 0.7, x1: scaleX(lastInSeries.date), x2: scaleX(series.prediction.date), y1: scaleY(lastInSeries.value), y2: scaleY(series.prediction.value) }))] }, series.name));
                            }), poisWithValues?.map((poi, i) => ((0, jsx_runtime_1.jsx)("circle", { cx: scaleX(poi.date), cy: poi.value !== undefined
                                    ? scaleY(poi.value)
                                    : HEIGHT - MARGINS.bottom, r: 3, fill: poi.color }, poi.date.toString() + poi.text + poi.color + i))), tooltipData &&
                                tooltipData.data.map((d) => d.dateValue && d.dateValue.value !== undefined ? ((0, jsx_runtime_1.jsx)("circle", { cx: scaleX(d.dateValue.date), cy: scaleY(d.dateValue.value), fill: d.color, r: 2 }, d.series)) : undefined)] }), tooltipData && (0, jsx_runtime_1.jsx)(Tooltip_1.Tooltip, { tooltipData: tooltipData })] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Legend, { children: [data.map((series) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.LegendItem, { color: series.color, text: series.name }, series.name))), limits?.map((limit) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.LegendItem, { color: limit.color, text: limit.label }, limit.label)))] })] }));
});
